import type { ReactNode } from 'react';

/* Une étape numérotée d'un écran de saisie.
 *
 * Jouer une balle, c'est toujours la même séquence : on regarde où l'on en
 * est, on choisit un club, on dit ce que la balle a fait. Sur le parcours
 * comme au practice.
 *
 * Ces trois moments existaient déjà dans les deux écrans, mais répartis sur
 * cinq blocs de même facture, sans rien qui dise lequel venait avant lequel.
 * Trois cartes, numérotées : la numérotation n'est pas un ornement, il y a
 * bien un ordre et c'est le même à chaque balle. Le joueur qui découvre
 * l'écran l'apprend une fois, celui qui le connaît retrouve son étape sans
 * lire — le chiffre en laiton se trouve avant le titre.
 *
 * Les en-têtes sont volontairement discrets : grisés, petits, en capitales
 * espacées. Ils structurent, ils ne parlent pas plus fort que le contenu — un
 * titre en corps de lecture aurait pris la place qu'on vient justement de
 * rendre au pavé.
 */
export function Etape({ n, titre, action, className = '', children }: {
    n: number;
    titre: string;
    /** Ce qui se pose à droite du titre, sur la même ligne. */
    action?: ReactNode;
    className?: string;
    children: ReactNode;
}) {
    return (
        <section className={`rounded-box border border-base-300 bg-base-100 p-2 shadow-sm ${className}`}>
            <header className="mb-1.5 flex shrink-0 items-center gap-2 px-0.5">
                <span aria-hidden="true"
                    className="flex size-5 shrink-0 items-center justify-center rounded-full bg-accent
                               text-[11px] font-bold leading-none text-accent-content">
                    {n}
                </span>
                <h2 className="flex-1 truncate text-[11px] font-bold uppercase tracking-[0.14em]"
                    style={{ color: 'var(--tuile-encre)' }}>
                    {titre}
                </h2>
                {action}
            </header>
            {children}
        </section>
    );
}

/* Une valeur du bandeau vert : le libellé au-dessus, le chiffre en dessous.
 *
 * Sur le panneau vert profond en tête d'écran — celui qui dit où l'on se
 * trouve, tour ou séance. Blanc sur vert, chiffres de tableau d'affichage,
 * et le laiton pour LA valeur qu'on vérifie.
 *
 * L'étiquette au-dessus et non à côté : quatre colonnes de deux étages
 * tiennent dans la largeur d'un téléphone, quatre paires « mot valeur » non.
 */
export function Valeur({ v, mot, accent = false }: { v: string; mot: string; accent?: boolean }) {
    return (
        <span className="min-w-0">
            <span className="block text-[10px] font-bold uppercase tracking-wide text-white/55">{mot}</span>
            <span className={`font-score block truncate text-xl leading-tight ${accent ? 'text-accent' : 'text-white'}`}>
                {v}
            </span>
        </span>
    );
}
